import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from './ui/select';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { DollarSign, Search, Globe, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CurrencySelector Component
 * 
 * Features:
 * - Display all 94 world currencies
 * - Quick access to popular currencies
 * - Search functionality
 * - Auto-suggestion when departure city changes
 */
const CurrencySelector = ({ 
  selectedCurrency = 'GBP', 
  onCurrencyChange,
  departureAirport = null,
  showLabel = true,
  size = 'default' // 'default' | 'small' | 'large'
}) => {
  const [currencies, setCurrencies] = useState([]);
  const [popularCurrencies, setPopularCurrencies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Currency suggestion dialog state
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [lastSuggestedAirport, setLastSuggestedAirport] = useState(null);

  // Fetch all currencies on mount
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetch(`${API_URL}/api/umrah-builder/currencies`);
        const data = await response.json();
        
        if (data.success) {
          setCurrencies(data.currencies || []);
          setPopularCurrencies(data.popular || []);
        }
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCurrencies();
  }, []);

  // Check for currency suggestion when departure airport changes
  useEffect(() => {
    if (!departureAirport || departureAirport === lastSuggestedAirport) return;
    // Skip the auto-suggestion if the user has already chosen a currency
    // manually – we don't want to second-guess their choice on every search.
    try {
      if (localStorage.getItem('flight380_currency_manual') === '1') return;
    } catch (_) { /* ignore */ }
    
    const checkCurrencySuggestion = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/umrah-builder/currencies/suggest/${departureAirport}?current_currency=${selectedCurrency}`
        );
        const data = await response.json();
        
        if (data.success && data.suggest_change) {
          setSuggestion(data);
          setShowSuggestionDialog(true);
          setLastSuggestedAirport(departureAirport);
        }
      } catch (error) {
        console.error('Failed to get currency suggestion:', error);
      }
    };
    
    checkCurrencySuggestion();
  }, [departureAirport, selectedCurrency, lastSuggestedAirport]);

  // Handle currency change from suggestion
  const handleAcceptSuggestion = () => {
    if (suggestion && onCurrencyChange) {
      onCurrencyChange(suggestion.suggested_currency);
    }
    try { localStorage.setItem('flight380_currency_manual', '1'); } catch (_) { /* ignore */ }
    setShowSuggestionDialog(false);
  };

  const handleDeclineSuggestion = () => {
    try { localStorage.setItem('flight380_currency_manual', '1'); } catch (_) { /* ignore */ }
    setShowSuggestionDialog(false);
  };

  // Filter currencies based on search
  const filteredCurrencies = currencies.filter(currency => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      currency.code.toLowerCase().includes(query) ||
      currency.name.toLowerCase().includes(query)
    );
  });

  // Get selected currency info
  const selectedCurrencyInfo = currencies.find(c => c.code === selectedCurrency) || {
    code: selectedCurrency,
    symbol: selectedCurrency,
    name: selectedCurrency
  };

  const sizeClasses = {
    small: 'h-8 text-sm',
    default: 'h-10',
    large: 'h-12 text-lg'
  };
  const compact = size === 'compact';
  const triggerCls = compact
    ? 'h-9 w-auto min-w-0 px-2.5 gap-1 rounded-full bg-white border border-slate-200 text-slate-800 text-xs font-bold shadow-none hover:border-[#FF6600] hover:text-[#FF6600] focus:ring-2 focus:ring-[#FF6600]/30 focus:ring-offset-0 data-[state=open]:border-[#FF6600] [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-slate-400 [&>svg]:ml-0'
    : `${sizeClasses[size]} min-w-[140px] bg-white hover:bg-[#F5F5F5] text-[#FF6600] border-0 font-bold shadow-sm focus:ring-2 focus:ring-white/60 focus:ring-offset-1 focus:ring-offset-[#FF6600] data-[state=open]:bg-[#F5F5F5] [&_svg]:text-[#FF6600]`;

  return (
    <>
      {/* Currency Selector */}
      <div className="flex flex-col gap-1">
        {showLabel && (
          <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
            <DollarSign className="w-4 h-4" />
            Currency
          </label>
        )}
        
        <Select value={selectedCurrency} onValueChange={onCurrencyChange}>
          <SelectTrigger
            data-testid="currency-selector-trigger"
            aria-label="Currency"
            className={triggerCls}
          >
            <SelectValue>
              {compact ? (
                <span className="flex items-center gap-1 leading-none">
                  <span className="text-[#FF6600]">{selectedCurrencyInfo.symbol}</span>
                  <span>{selectedCurrency}</span>
                </span>
              ) : (
                <span className="flex items-center gap-2 text-[#222222]">
                  <span className="font-semibold text-[#222222]">{selectedCurrencyInfo.symbol}</span>
                  <span className="text-[#222222]">{selectedCurrency}</span>
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          
          <SelectContent className="max-h-[400px]">
            {/* Search Input */}
            <div className="p-2 border-b sticky top-0 bg-white z-10">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search currencies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8"
                />
              </div>
            </div>

            {/* Popular Currencies */}
            {!searchQuery && popularCurrencies.length > 0 && (
              <SelectGroup>
                <SelectLabel className="flex items-center gap-1 text-amber-600">
                  <Globe className="w-3 h-3" />
                  Popular Currencies
                </SelectLabel>
                {popularCurrencies.map((currency) => (
                  <SelectItem key={`popular-${currency.code}`} value={currency.code}>
                    <span className="flex items-center gap-2 w-full">
                      <span className="font-bold w-8">{currency.symbol}</span>
                      <span className="font-medium">{currency.code}</span>
                      <span className="text-gray-500 text-sm truncate">{currency.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}

            {/* All Currencies (or filtered) */}
            <SelectGroup>
              <SelectLabel>
                {searchQuery ? `Results for "${searchQuery}"` : 'All Currencies'}
              </SelectLabel>
              {filteredCurrencies.map((currency) => (
                <SelectItem key={currency.code} value={currency.code}>
                  <span className="flex items-center gap-2 w-full">
                    <span className="font-bold w-8">{currency.symbol}</span>
                    <span className="font-medium">{currency.code}</span>
                    <span className="text-gray-500 text-sm truncate">{currency.name}</span>
                  </span>
                </SelectItem>
              ))}
              
              {filteredCurrencies.length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  No currencies found for &quot;{searchQuery}&quot;
                </div>
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Currency Suggestion Dialog */}
      <Dialog open={showSuggestionDialog} onOpenChange={setShowSuggestionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              {suggestion?.prompt_title || 'Change Currency?'}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {suggestion?.prompt_text}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex items-center justify-center gap-4">
              {/* Current Currency */}
              <div className="text-center p-4 bg-gray-100 rounded-lg">
                <div className="text-2xl font-bold">{suggestion?.current_currency_symbol}</div>
                <div className="text-sm text-gray-600">{suggestion?.current_currency}</div>
                <Badge variant="secondary" className="mt-1">Current</Badge>
              </div>
              
              {/* Arrow */}
              <div className="text-gray-400">→</div>
              
              {/* Suggested Currency */}
              <div className="text-center p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{suggestion?.suggested_currency_symbol}</div>
                <div className="text-sm text-blue-700">{suggestion?.suggested_currency}</div>
                <Badge className="mt-1 bg-blue-600">Suggested</Badge>
              </div>
            </div>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              Based on your departure from <strong>{departureAirport}</strong>
            </p>
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleDeclineSuggestion}
              className="flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              {suggestion?.cancel_button || `Keep ${selectedCurrency}`}
            </Button>
            <Button
              onClick={handleAcceptSuggestion}
              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700"
            >
              <Check className="w-4 h-4" />
              {suggestion?.confirm_button || `Use ${suggestion?.suggested_currency}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Compact Currency Badge - For displaying currency in headers/summaries
 */
export const CurrencyBadge = ({ currency, onClick, className = '' }) => {
  const [currencyInfo, setCurrencyInfo] = useState(null);

  useEffect(() => {
    const fetchCurrencyInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/umrah-builder/currencies/${currency}`);
        const data = await response.json();
        if (data.success) {
          setCurrencyInfo(data.currency);
        }
      } catch (error) {
        console.error('Failed to fetch currency info:', error);
      }
    };
    
    if (currency) {
      fetchCurrencyInfo();
    }
  }, [currency]);

  return (
    <Badge 
      variant="outline" 
      className={`cursor-pointer hover:bg-gray-100 ${className}`}
      onClick={onClick}
    >
      <DollarSign className="w-3 h-3 mr-1" />
      {currencyInfo?.symbol || currency} {currency}
    </Badge>
  );
};

/**
 * Format price with currency symbol
 */
export const formatPrice = (amount, currency = 'GBP', currencyInfo = null) => {
  const symbols = {
    'GBP': '£', 'USD': '$', 'EUR': '€', 'SAR': 'ر.س', 'AED': 'د.إ',
    'PKR': '₨', 'INR': '₹', 'BDT': '৳', 'CAD': 'C$', 'AUD': 'A$',
    'SGD': 'S$', 'MYR': 'RM', 'QAR': 'ر.ق', 'KWD': 'د.ك', 'JPY': '¥',
    'CNY': '¥', 'THB': '฿', 'IDR': 'Rp', 'PHP': '₱', 'KRW': '₩'
  };
  
  const symbol = currencyInfo?.symbol || symbols[currency] || currency;
  const decimalPlaces = currencyInfo?.decimal_places ?? 2;
  
  // Format number with commas and proper decimal places
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
  
  return `${symbol}${formatted}`;
};

export default CurrencySelector;
